import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { History, ArrowLeftRight, Check } from "lucide-react";

const QS = ["q1", "q2", "q3"];
const UI = {
  tr: { btn: "Sürümler", title: "Taslak Sürümleri", desc: "Her yeniden üretmeden önce mevcut taslak saklanır (dil bazlı, en fazla 5). Eski sürümü soru bazında veya tümüyle geri alabilirsiniz.", current: "Mevcut taslak", version: "Sürüm", useAll: "Tümünü kullan", useThis: "Bunu kullan", empty: "Bu dilde saklanan sürüm yok.", chars: "karakter", q: ["1. Akademik Motivasyon", "2. Akademik Hazırlık", "3. Eğitim Dışı Deneyimler"] },
  en: { btn: "Versions", title: "Draft Versions", desc: "Before each regeneration the current draft is stored (per language, max 5). Restore an older version per question or entirely.", current: "Current draft", version: "Version", useAll: "Use all", useThis: "Use this", empty: "No stored versions in this language.", chars: "characters", q: ["1. Academic Motivation", "2. Academic Preparation", "3. Outside Education"] },
};

export const StatementVersions = ({ lang, versions, current, onRestore }) => {
  const u = UI[lang];
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const list = [...versions].filter((v) => v.language === lang).reverse();
  const v = list[sel];
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(lang === "tr" ? "tr-TR" : "en-GB", { dateStyle: "short", timeStyle: "short" }) : "");

  return (
    <>
      <Button data-testid="statement-versions-btn" variant="outline" onClick={() => { setSel(0); setOpen(true); }} disabled={list.length === 0} className="border-[#1a2e4a] text-[#1a2e4a] h-11">
        <History className="h-4 w-4 mr-2" /> {u.btn} ({list.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="versions-dialog" className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1a2e4a] flex items-center gap-2"><ArrowLeftRight className="h-5 w-5 text-[#c0392b]" /> {u.title}</DialogTitle>
            <DialogDescription className="text-[#64748b]">{u.desc}</DialogDescription>
          </DialogHeader>
          {list.length === 0 ? <div className="text-sm text-[#64748b]">{u.empty}</div> : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {list.map((x, i) => (
                  <button key={x.id} data-testid={`version-tab-${i}`} onClick={() => setSel(i)} className={`text-xs rounded-full px-3 py-1 border transition-colors ${i === sel ? "bg-[#1a2e4a] text-white border-[#1a2e4a]" : "text-[#1a2e4a] border-[#cbd5e1] hover:bg-[#f1f5f9]"}`}>
                    {u.version} {list.length - i} · {fmt(x.createdAt)}
                  </button>
                ))}
                <Button data-testid="version-use-all" size="sm" onClick={() => { onRestore(v.answers); setOpen(false); }} className="ml-auto bg-[#c0392b] hover:bg-[#a93226] text-white h-8"><Check className="h-3.5 w-3.5 mr-1" /> {u.useAll}</Button>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-2">
                <div className="text-xs font-bold uppercase tracking-wide text-[#1a2e4a]">{u.current}</div>
                <div className="text-xs font-bold uppercase tracking-wide text-[#c0392b] hidden md:block">{u.version} {list.length - sel}</div>
                {QS.map((q, i) => (
                  <div key={q} className="contents">
                    <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-3">
                      <div className="text-[12px] font-bold text-[#1a2e4a] mb-1">{u.q[i]}</div>
                      <p data-testid={`version-current-${q}`} className="text-[12.5px] text-[#334155] whitespace-pre-wrap leading-relaxed">{current[q] || "—"}</p>
                      <div className="text-[10px] text-[#94a3b8] mt-1">{(current[q] || "").length} {u.chars}</div>
                    </div>
                    <div className="rounded-lg border border-[#c0392b]/30 bg-[#fadbd8]/20 p-3">
                      <div className="text-[12px] font-bold text-[#c0392b] mb-1 flex items-center justify-between">{u.q[i]}
                        <button data-testid={`version-use-${q}`} onClick={() => onRestore({ [q]: v.answers[q] })} className="text-[11px] font-semibold text-[#c0392b] hover:underline">{u.useThis} →</button>
                      </div>
                      <p data-testid={`version-old-${q}`} className="text-[12.5px] text-[#334155] whitespace-pre-wrap leading-relaxed">{v.answers[q] || "—"}</p>
                      <div className="text-[10px] text-[#94a3b8] mt-1">{(v.answers[q] || "").length} {u.chars}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
